import React, { useState } from 'react';
import qrService, { QRCreateResponse } from '../../../services/qrService';
import toast from 'react-hot-toast';

const QRCodeTab: React.FC = () => {
  const [originalUrl, setOriginalUrl] = useState('');
  const [title, setTitle] = useState('');
  const [customCode, setCustomCode] = useState('');
  const [size, setSize] = useState(512);
  const [margin, setMargin] = useState(4);
  const [fgColor, setFgColor] = useState('#000000');
  const [bgColor, setBgColor] = useState('#FFFFFF');
  const [result, setResult] = useState<QRCreateResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const res = await qrService.create({
        original_url: originalUrl,
        custom_code: customCode || undefined,
        title: title || undefined,
        qr_options: {
          size,
          margin,
          fg_color: fgColor,
          bg_color: bgColor
        }
      });
      setResult(res);
      toast.success('QR code generated');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to generate QR code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Info Box */}
      <div className="rounded-xl bg-purple-50 p-5 shadow-sm ring-1 ring-purple-100">
        <h3 className="mb-2 text-base font-semibold text-purple-900">Generate Custom QR Codes</h3>
        <p className="text-sm text-purple-800">
          Create scannable QR codes for your URLs. Optionally shorten long URLs and customize colors, size, and margin.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Form */}
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="rounded-xl bg-gray-50 p-5 shadow-sm ring-1 ring-gray-200">
            <h4 className="mb-4 text-base font-semibold text-gray-900">QR Code Settings</h4>
            
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Original URL *</label>
                <input
                  type="url"
                  required
                  value={originalUrl}
                  onChange={(e) => setOriginalUrl(e.target.value)}
                  placeholder="https://example.com/page?utm=..."
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="My Spring Promo"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Custom Short Code</label>
                <input
                  type="text"
                  value={customCode}
                  onChange={(e) => setCustomCode(e.target.value)}
                  placeholder="3-10 letters/numbers"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
                <span className="mt-1 block text-xs text-gray-500">Used only if URL needs shortening; must be unique</span>
              </div>
            </div>
          </div>

          <div className="rounded-xl bg-gray-50 p-5 shadow-sm ring-1 ring-gray-200">
            <h4 className="mb-4 text-base font-semibold text-gray-900">Appearance</h4>
            
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Size (px)</label>
                  <input
                    type="number"
                    min={128}
                    max={2048}
                    value={size}
                    onChange={(e) => setSize(Number(e.target.value))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                  <span className="mt-1 block text-xs text-gray-500">128-2048 pixels</span>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Margin</label>
                  <input
                    type="number"
                    min={0}
                    max={16}
                    value={margin}
                    onChange={(e) => setMargin(Number(e.target.value))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                  <span className="mt-1 block text-xs text-gray-500">0-16 modules</span>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Foreground Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={fgColor}
                      onChange={(e) => setFgColor(e.target.value)}
                      className="h-10 w-16 rounded-lg border border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={fgColor}
                      onChange={(e) => setFgColor(e.target.value)}
                      className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Background Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={bgColor}
                      onChange={(e) => setBgColor(e.target.value)}
                      className="h-10 w-16 rounded-lg border border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={bgColor}
                      onChange={(e) => setBgColor(e.target.value)}
                      className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary/90 disabled:opacity-50"
          >
            {loading ? 'Generating...' : 'Generate QR Code'}
          </button>
        </form>

        {/* Preview */}
        <div>
          {result ? (
            <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-200">
              <h4 className="mb-4 text-base font-semibold text-gray-900">QR Code Preview</h4>
              
              {/* URL Info */}
              <div className="mb-4 rounded-lg bg-gray-50 p-3">
                <div className="text-xs font-medium text-gray-600">Target URL:</div>
                <div className="mt-1 text-sm break-all">
                  {result.was_shortened && result.short_url ? (
                    <div className="flex items-center gap-2">
                      <span className="inline-block rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                        Shortened
                      </span>
                      <a
                        href={result.short_url}
                        className="text-blue-600 hover:underline"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {result.short_url}
                      </a>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="inline-block rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800">
                        Direct
                      </span>
                      <span className="text-gray-800">{result.original_url}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* QR Image */}
              <div className="mb-4 flex justify-center">
                {result.preview_url ? (
                  <img
                    src={result.preview_url}
                    alt="QR code preview"
                    className="max-w-full h-auto rounded-lg border border-gray-200 shadow-sm"
                  />
                ) : (
                  <div className="text-sm text-gray-500">No preview available</div>
                )}
              </div>

              {/* Actions */}
              {result.preview_url && (
                <div className="flex gap-2">
                  <a
                    href={result.preview_url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-gray-800 px-4 py-2 text-sm font-semibold text-white transition hover:bg-black"
                  >
                    📥 Open Image
                  </a>
                </div>
              )}
            </div>
          ) : (
            <div className="flex h-full items-center justify-center rounded-xl bg-gray-50 p-12 text-center ring-1 ring-gray-200">
              <div>
                <div className="mb-3 text-4xl">📱</div>
                <p className="text-sm text-gray-500">Fill in the form and generate to see your QR code preview</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QRCodeTab;
